import { CheckCircle2, Eye, File, UploadCloud, X } from "lucide-react";
import { useRef, useState } from "react";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import Select from "@/components/ui/Select";
import { FieldWrapper } from "@/components/ui/FieldWrapper";

interface DocTypeOption {
  value: string;
  label: string;
}

interface Props {
  id?: string;
  label: string;
  helper: string;
  docTypeOptions: DocTypeOption[];
  docTypeValue: string;
  onDocTypeChange: (v: string) => void;
  fileValue: string;
  onFileChange: (fileName: string) => void;
  fileUrlValue?: string;
  onFileUrlChange?: (url: string) => void;
  error?: string;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

export default function UploadBlock({
  id,
  label,
  helper,
  docTypeOptions,
  docTypeValue,
  onDocTypeChange,
  fileValue,
  onFileChange,
  fileUrlValue,
  onFileUrlChange,
  error,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState(fileUrlValue || "");
  const [fileName, setFileName] = useState(fileValue);
  const [fileSize, setFileSize] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [docTypeError, setDocTypeError] = useState("");

  function handleUploadClick() {
    if (!docTypeValue || !docTypeValue.trim()) {
      setDocTypeError("Please select a document type before uploading");
      return;
    }
    setDocTypeError("");
    inputRef.current?.click();
  }

  function handleFile(file?: File | null) {
    if (!docTypeValue || !docTypeValue.trim()) {
      setDocTypeError("Please select a document type before uploading");
      return;
    }
    if (!file) return;
    setDocTypeError("");
    setFileName(file.name);
    setFileSize(formatBytes(file.size));
    onFileChange(file.name);

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      setPreviewUrl(dataUrl);
      onFileUrlChange?.(dataUrl);
    };
    reader.readAsDataURL(file);
  }

  return (
    <div id={id} className="rounded-[10px] border border-line bg-white p-4">
      <div className="mb-1.5 flex items-center gap-1">
        <span className="text-sm font-medium text-ink">{label}</span>
        <span className="text-danger">*</span>
      </div>
      <p className="text-xs text-ink-secondary">{helper}</p>

      <div className="mt-3">
        <Select
          id={id ? `${id}-select` : undefined}
          label="Document Type"
          required
          placeholder="Select document type"
          options={docTypeOptions}
          value={docTypeValue}
          onChange={(e) => {
            onDocTypeChange(e.target.value);
            if (e.target.value) setDocTypeError("");
          }}
          error={docTypeError || (!fileName && error ? error : undefined)}
        />
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf"
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />

      {fileName ? (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-line bg-page px-3 py-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white text-ink-muted">
            <File className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-ink">{fileName}</p>
            <p className="text-xs text-ink-secondary">{fileSize}</p>
          </div>
          <CheckCircle2 className="h-5 w-5 shrink-0 text-success" />
          <button
            type="button"
            onClick={() => {
              if (previewUrl) setPreviewOpen(true);
              else if (fileName) setPreviewOpen(true);
            }}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
            aria-label="Preview document"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              setFileName("");
              setFileSize("");
              setPreviewUrl("");
              onFileChange("");
              onFileUrlChange?.("");
            }}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-danger cursor-pointer"
            aria-label="Remove document"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div>
          <button
            type="button"
            onClick={handleUploadClick}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFile(e.dataTransfer.files?.[0]);
            }}
            className={`mt-3 flex w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed px-4 py-6 text-center transition-colors cursor-pointer ${
              dragOver
                ? "border-primary-500 bg-primary-50"
                : docTypeError
                  ? "border-danger bg-danger/5 hover:border-danger hover:bg-danger/10"
                  : !docTypeValue
                    ? "border-slate-200 bg-slate-50/70 hover:border-primary-300 hover:bg-slate-100/70"
                    : "border-slate-300 hover:border-primary-400 hover:bg-page"
            }`}
          >
            <UploadCloud className={`h-6 w-6 ${docTypeError ? "text-danger" : "text-ink-muted"}`} />
            <span className="text-sm text-ink">
              {!docTypeValue ? "Select document type above to upload" : "Click to upload or drag and drop"}
            </span>
            <span className="text-xs text-ink-secondary">JPG, PNG or PDF, maximum 5 MB</span>
          </button>
          {(docTypeError || (!fileName && error)) && (
            <p className="mt-1.5 text-xs font-medium text-danger">{docTypeError || error}</p>
          )}
        </div>
      )}

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title={fileName || "Document preview"} size="md" footer={<div className="flex items-center justify-end border-t border-line px-5 py-4"><Button variant="secondary" onClick={() => setPreviewOpen(false)}>Close</Button></div>}>
        {previewUrl ? (
          <img src={previewUrl} alt={fileName} className="max-h-[60vh] w-full rounded-lg object-contain bg-[#0F172A]" />
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <File className="h-10 w-10 text-ink-muted" />
            <p className="text-sm text-ink">{fileName}</p>
            <p className="text-xs text-ink-secondary">PDF documents cannot be previewed here.</p>
          </div>
        )}
      </Modal>
    </div>
  );
}